import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Coins, Gavel, AlertCircle } from "lucide-react";
import { contractsApi, invoicesApi } from "../lib/ledgerApi";
import { useAuth } from "../contexts/AuthContext";

const fmt = (s) => {
  try {
    const n = Number(s);
    return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : s;
  } catch { return s; }
};

export default function ContractsCard() {
  const { business } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    if (!business) return;
    let alive = true;
    const tick = async () => {
      try {
        const [cs, is] = await Promise.all([
          contractsApi.list({ state: "active" }).catch(() => []),
          invoicesApi.list({ role: "received", status: "issued" }).catch(() => []),
        ]);
        if (alive) {
          setContracts(cs);
          setInvoices(is);
        }
      } catch {}
    };
    tick();
    const i = setInterval(tick, 6000);
    return () => { alive = false; clearInterval(i); };
  }, [business]);

  const totalsByCurrency = {};
  for (const c of contracts) {
    const k = c.currency;
    totalsByCurrency[k] = (totalsByCurrency[k] || 0) + Number(c.total_locked_amount);
  }
  const disputed = contracts.filter((c) => c.state === "disputed" && c.arbiter_business_id === business?.id);
  const myClaims = contracts.filter(
    (c) => c.contract_type === "streaming" && c.counterparty_business_id === business?.id
  );

  const isEmpty = contracts.length === 0 && invoices.length === 0;

  return (
    <div className="border border-border rounded-sm bg-card p-5" data-testid="contracts-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Active contracts</div>
        </div>
        <Link to="/app/contracts" className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground" data-testid="contracts-card-all-link">
          all →
        </Link>
      </div>

      <div className="text-3xl font-mono" data-testid="contracts-card-count">{contracts.length}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
        {contracts.length === 1 ? "in flight" : "in flight"}
      </div>

      {Object.keys(totalsByCurrency).length > 0 && (
        <div className="mt-3 pt-3 border-t border-border space-y-1" data-testid="contracts-card-locked">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Locked</div>
          {Object.entries(totalsByCurrency).map(([k, v]) => (
            <div key={k} className="text-xs font-mono flex justify-between">
              <span>{k}</span>
              <span>{fmt(v)}</span>
            </div>
          ))}
        </div>
      )}

      {(invoices.length > 0 || disputed.length > 0 || myClaims.length > 0) && (
        <div className="mt-3 pt-3 border-t border-border space-y-1.5" data-testid="contracts-card-pending">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Pending</div>
          {invoices.length > 0 && (
            <Link to="/app/invoices" className="flex items-center gap-1.5 text-xs hover:underline" data-testid="dash-pending-invoices">
              <Coins className="w-3 h-3 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
              {invoices.length} invoice{invoices.length === 1 ? "" : "s"} to approve
            </Link>
          )}
          {disputed.length > 0 && (
            <Link to="/app/contracts" className="flex items-center gap-1.5 text-xs hover:underline" data-testid="dash-pending-disputes">
              <Gavel className="w-3 h-3 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
              {disputed.length} dispute{disputed.length === 1 ? "" : "s"} to resolve
            </Link>
          )}
          {myClaims.length > 0 && (
            <Link to="/app/contracts?role=counterparty" className="flex items-center gap-1.5 text-xs hover:underline" data-testid="dash-pending-claims">
              <AlertCircle className="w-3 h-3 text-status-info" strokeWidth={1.5} />
              {myClaims.length} stream{myClaims.length === 1 ? "" : "s"} claimable
            </Link>
          )}
        </div>
      )}

      {isEmpty && (
        <div className="mt-3 pt-3 border-t border-border" data-testid="contracts-card-empty">
          <div className="text-xs text-muted-foreground mb-2">No active contracts.</div>
          <Link
            to="/app/contracts/new"
            data-testid="contracts-card-cta"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] uppercase tracking-widest border border-border rounded-sm hover:border-foreground hover:bg-muted transition-colors"
          >
            <FileText className="w-3 h-3" strokeWidth={1.5} /> Draft one
          </Link>
        </div>
      )}
    </div>
  );
}
